import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  CssBaseline,
  Avatar,
  Stack,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import BookOnlineIcon from "@mui/icons-material/BookOnline";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LogoutIcon from "@mui/icons-material/Logout";
import LockIcon from "@mui/icons-material/Lock";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ApartmentIcon from "@mui/icons-material/Apartment";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import config from "../../Config";
// import HomeslyLogo from "../../assets/homesly-logo.png";

const drawerWidth = 240;

const navItems = [
  { label: "Dashboard", icon: <DashboardIcon />, path: "/dashboard" },
  { label: "Booking", icon: <BookOnlineIcon />, path: "/booking" },
  { label: "Product Management", icon: <Inventory2Icon />, path: "/products" },
  { label: "Upsell Management", icon: <FastfoodIcon />, path: "/upsell" },
  { label: "Order", icon: <ShoppingCartIcon />, path: "/orders" },
  { label: "TT Lock", icon: <LockIcon />, path: "/lock-management" },
  { label: "Admin Management", icon: <AdminPanelSettingsIcon />, path: "/admin-management" },
  { label: "Property Management", icon: <ApartmentIcon />, path: "/property-management" },
  { label: "Leads", icon: <ContactMailIcon />, path: "/leads" },
  { label: "Logout", icon: <LogoutIcon />, path: "/", logout: true },
];

export default function SideNav({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("md"));
  const isXsDown = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  const userId = localStorage.getItem("userId");
  const role = localStorage.getItem("role");
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavClick = (item) => {
    if (item.logout) {
      localStorage.clear();
      navigate(item.path);
    } else {
      navigate(item.path);
    }
    if (isSmDown) setMobileOpen(false);
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenPasswordDialog = () => {
    handleMenuClose();
    setPasswordDialogOpen(true);
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setOldPassword("");
    setNewPassword("");
    setShowOldPassword(false);
    setShowNewPassword(false);
    setPasswordErrors({});
  };

  const validatePasswordForm = () => {
    const errors = {};

    if (!oldPassword.trim()) {
      errors.oldPassword = "Old password is required";
    }

    if (!newPassword.trim()) {
      errors.newPassword = "New password is required";
    } else if (newPassword.trim().length < 6) {
      errors.newPassword = "New password must be at least 6 characters";
    }

    if (oldPassword && newPassword && oldPassword === newPassword) {
      errors.newPassword = "New password must be different from old password";
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChangePassword = async () => {
    if (!validatePasswordForm()) return;

    try {
      setChangePasswordLoading(true);
      const token = localStorage.getItem("token");

      const res = await fetch(`${config.apiBaseUrl}/admin/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: oldPassword,
          newPassword,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data.message || "Password changed successfully.",
        });
        handleClosePasswordDialog();
      } else {
        throw new Error(data.message || "Failed to change password");
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const drawerContent = (
    <Box sx={{ pt: 2 }}>
      <List>
        {navItems.map((item) => (
          <ListItem
            button
            key={item.label}
            onClick={() => handleNavClick(item)}
            sx={{ cursor: "pointer" }}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const drawerTop = isXsDown ? 56 : 64;
  const drawerHeight = `calc(100vh - ${drawerTop}px)`;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          width: "100%",
          left: 0,
          top: 0,
        }}
      >
        <Toolbar
          sx={{
            minHeight: drawerTop,
            display: "flex",
            justifyContent: "space-between",
            px: { xs: 1.5, sm: 2, md: 3 },
            gap: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            {isSmDown && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: { xs: 0.5, sm: 1 } }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* <img
              src={HomeslyLogo}
              alt="Homesly Logo"
              style={{ width: 36, height: 36, marginRight: 6 }}
            /> */}

            <Typography
              variant="h6"
              noWrap
              component="div"
              onClick={() => navigate("/dashboard")}
              sx={{
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
              }}
            >
               Admin Panel
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Box
              textAlign="right"
              sx={{
                display: { xs: "none", sm: "block" },
              }}
            >
              <Typography variant="body1" fontWeight={600} noWrap>
                {userId || "N/A"}
              </Typography>

              <Typography variant="body2" color="white" noWrap>
                {role || "Role"}
              </Typography>
            </Box>

            <IconButton onClick={handleAvatarClick} size="small" sx={{ p: 0 }}>
              <Avatar sx={{ bgcolor: "grey", color: "white", fontWeight: 600 }}>
                {(role && role.charAt(0)) || "U"}
              </Avatar>
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem onClick={handleOpenPasswordDialog}>
                <ListItemIcon>
                  <LockOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Change Password
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleNavClick({ logout: true, path: "/" });
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Stack>
        </Toolbar>
      </AppBar>

      <Drawer
        variant={isSmDown ? "temporary" : "permanent"}
        open={isSmDown ? mobileOpen : true}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            top: `${drawerTop}px`,
            height: drawerHeight,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          pt: `${drawerTop}px`,
          ml: { md: `${drawerWidth}px` },
        }}
      >
        {children}
      </Box>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog} fullWidth maxWidth="xs">
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              fullWidth
              label="Current Password"
              type={showOldPassword ? "text" : "password"}
              value={oldPassword}
              onChange={(e) => {
                setOldPassword(e.target.value);
                if (passwordErrors.oldPassword) {
                  setPasswordErrors((prev) => ({ ...prev, oldPassword: "" }));
                }
              }}
              error={Boolean(passwordErrors.oldPassword)}
              helperText={passwordErrors.oldPassword || " "}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowOldPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showOldPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (passwordErrors.newPassword) {
                  setPasswordErrors((prev) => ({ ...prev, newPassword: "" }));
                }
              }}
              error={Boolean(passwordErrors.newPassword)}
              helperText={passwordErrors.newPassword || " "}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      edge="end"
                      size="small"
                    >
                      {showNewPassword ? (
                        <VisibilityOffIcon fontSize="small" />
                      ) : (
                        <VisibilityIcon fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClosePasswordDialog} disabled={changePasswordLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleChangePassword}
            disabled={changePasswordLoading}
          >
            {changePasswordLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              "Update Password"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}